import {
  CheckSquare2,
  Columns3,
  Grid3X3,
  File,
  Heading1,
  Image,
  Link2,
  MessageCircle,
  Paintbrush,
  PanelsTopLeft,
  StickyNote,
  Type,
} from "lucide-react";
import type { EditorNodeData, NodePlugin } from "./editor-types";
import { componentRegistry } from "./component-registry";
import {
  ChecklistNode,
  BoardPortalNode,
  ColumnNode,
  CommentNode,
  FileNode,
  HeadingNode,
  ImageNode,
  LinkNode,
  NoteNode,
  NoteDocumentWindow,
  TextNode,
  SketchNode,
  TableNode,
} from "../features/nodes/BuiltinNodes";
import { getMarkdownAssetHashes } from "../features/documents/markdown-assets";

const nowData = (
  kind: string,
  title: string,
  overrides?: Partial<EditorNodeData>,
): EditorNodeData => {
  const now = new Date().toISOString();
  return {
    kind,
    pluginVersion: 1,
    title,
    color: "paper",
    createdAt: now,
    updatedAt: now,
    ...overrides,
  };
};

export const builtinComponents: NodePlugin[] = [
  {
    type: "note",
    version: 1,
    title: "便签",
    description: "快速记录灵感",
    icon: StickyNote,
    defaultSize: { width: 240, height: 190 },
    declarations: { interaction: { hasBuiltInDragHandle: false } },
    create: (overrides) => nowData("note", "便签", { color: "paper", content: "", ...overrides }),
    Renderer: NoteNode,
    getAssetRefs: (data) => getMarkdownAssetHashes(data.content ?? ""),
    documentWindow: {
      Renderer: NoteDocumentWindow,
      defaultSize: { width: 720, height: 560 },
      minSize: { width: 460, height: 320 },
      kindLabel: "Markdown",
      getTitle: (data) => data.title || "便签",
    },
  },
  {
    type: "text",
    version: 1,
    title: "文本",
    description: "自由文本块",
    icon: Type,
    defaultSize: { width: 260, height: 120 },
    declarations: { interaction: { hasBuiltInDragHandle: false } },
    create: (overrides) => nowData("text", "文本", { content: "", ...overrides }),
    Renderer: TextNode,
  },
  {
    type: "heading",
    version: 1,
    title: "标题",
    description: "为区域添加标题",
    icon: Heading1,
    defaultSize: { width: 360, height: 100 },
    declarations: { interaction: { hasBuiltInDragHandle: false } },
    create: (overrides) => nowData("heading", "标题", { content: "新标题", ...overrides }),
    Renderer: HeadingNode,
  },
  {
    type: "checklist",
    version: 1,
    title: "清单",
    description: "可勾选的任务列表",
    icon: CheckSquare2,
    defaultSize: { width: 260, height: 220 },
    declarations: { interaction: { hasBuiltInDragHandle: true } },
    create: (overrides) => nowData("checklist", "待办清单", {
      items: [
        { id: crypto.randomUUID(), text: "第一个事项", checked: false },
        { id: crypto.randomUUID(), text: "完成后勾选", checked: true },
      ],
      ...overrides,
    }),
    Renderer: ChecklistNode,
  },
  {
    type: "link",
    version: 1,
    title: "链接",
    description: "保存网页和参考资料",
    icon: Link2,
    defaultSize: { width: 280, height: 180 },
    declarations: { interaction: { hasBuiltInDragHandle: true } },
    create: (overrides) => nowData("link", "网页标题", { url: "", ...overrides }),
    Renderer: LinkNode,
  },
  {
    type: "image",
    version: 1,
    title: "图片",
    description: "本地图片与视觉参考",
    icon: Image,
    defaultSize: { width: 300, height: 260 },
    declarations: { interaction: { hasBuiltInDragHandle: false } },
    create: (overrides) => nowData("image", "", { ...overrides }),
    Renderer: ImageNode,
  },
  {
    type: "file",
    version: 1,
    title: "文件",
    description: "附加任意本地文件",
    icon: File,
    defaultSize: { width: 280, height: 126 },
    declarations: { interaction: { hasBuiltInDragHandle: true } },
    create: (overrides) => nowData("file", "文件", { ...overrides }),
    Renderer: FileNode,
  },
  {
    type: "column",
    version: 1,
    title: "分区",
    description: "组织一组相关内容",
    icon: Columns3,
    defaultSize: { width: 320, height: 360 },
    declarations: {
      interaction: { hasBuiltInDragHandle: true },
      availability: { creatable: false },
    },
    create: (overrides) => nowData("column", "新分区", { color: "sky", content: "", ...overrides }),
    Renderer: ColumnNode,
  },
  {
    type: "table",
    version: 1,
    title: "表格",
    description: "结构化整理信息",
    icon: Grid3X3,
    defaultSize: { width: 390, height: 260 },
    declarations: { interaction: { hasBuiltInDragHandle: true } },
    create: (overrides) => nowData("table", "数据表", {
      table: [["名称", "状态", "备注"], ["项目 A", "进行中", ""], ["项目 B", "待开始", ""]],
      ...overrides,
    }),
    Renderer: TableNode,
  },
  {
    type: "sketch",
    version: 1,
    title: "手绘",
    description: "自由勾画草图",
    icon: Paintbrush,
    defaultSize: { width: 360, height: 300 },
    declarations: { interaction: { hasBuiltInDragHandle: true } },
    create: (overrides) => nowData("sketch", "草图", { strokes: [], ...overrides }),
    Renderer: SketchNode,
  },
  {
    type: "comment",
    version: 1,
    title: "评论",
    description: "为想法补充讨论",
    icon: MessageCircle,
    defaultSize: { width: 250, height: 150 },
    declarations: { interaction: { hasBuiltInDragHandle: true } },
    create: (overrides) => nowData("comment", "评论", { color: "rose", content: "", ...overrides }),
    Renderer: CommentNode,
  },
  {
    type: "board",
    version: 1,
    title: "Board",
    description: "创建子画板入口；复制粘贴可创建快捷方式",
    icon: PanelsTopLeft,
    defaultSize: { width: 270, height: 178 },
    declarations: { interaction: { hasBuiltInDragHandle: true } },
    create: (overrides) => nowData("board", "新画板", { color: "lavender", ...overrides }),
    Renderer: BoardPortalNode,
  },
];

export function registerBuiltinComponents(): void {
  for (const plugin of builtinComponents) {
    if (!componentRegistry.get(plugin.type)) componentRegistry.register(plugin);
  }
}
